import { useState, useEffect } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { ArrowLeft, Edit, Save, X, ChevronRight, ChevronLeft, CheckCircle, AlertCircle } from "lucide-react"
import api from "../utils/apiClient"
import VanillaQuillEditor from "../components/editor/VanillaQuillEditor"
import { motion, AnimatePresence } from "framer-motion"
import { contentPublishService } from "../services/contentPublishService"
import { useProjects } from "../contexts/ProjectContext"

const GeneratedContentView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { currentProject } = useProjects()
  const [loading, setLoading] = useState(true)
  const [content, setContent] = useState(null)
  const [error, setError] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [htmlContent, setHtmlContent] = useState("")
  const [title, setTitle] = useState("")
  const [metaDescription, setMetaDescription] = useState("")
  const [slug, setSlug] = useState("")
  const [saving, setSaving] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [notification, setNotification] = useState(null)
  const [integrations, setIntegrations] = useState([])
  const [selectedIntegration, setSelectedIntegration] = useState("")
  const [publishing, setPublishing] = useState(false)
  const [contentStatus, setContentStatus] = useState("published")
  const [statusUpdating, setStatusUpdating] = useState(false)

  useEffect(() => {
    // Function to fetch content
    const fetchContent = async () => {
      try {
        setLoading(true)

        // Fetch the content from API
        const response = await api.blogGeneration.getGeneratedContent(id)
        const contentData = response.data

        // Add thumbnail_url and content_url to the content data if they exist
        setContent({
          ...contentData,
          thumbnail_url: contentData.thumbnail_url || null,
          content_url: contentData.content_url || null
        })
        
        setTitle(contentData.title)
        setMetaDescription(contentData.meta_description)
        setHtmlContent(contentData.content)
        setSlug(contentData.slug || contentData.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""))
        setContentStatus(contentData.status || "published")

        setLoading(false)
      } catch (error) {
        console.error("Error fetching generated content:", error)
        setError("Failed to load content. It may have been deleted or you don't have permission to view it.")
        setLoading(false)
      }
    }

    fetchContent()
    
    // Fetch integrations if we have a current project
    if (currentProject?.id) {
      fetchIntegrations()
    }
  }, [id, currentProject?.id])

  const fetchIntegrations = async () => {
    try {
      const integrationsList = await contentPublishService.getAvailableIntegrations(currentProject.id)
      setIntegrations(integrationsList)
      
      // Check if there are any integrations available
      if (integrationsList.length > 0) {
        // Check if the currently selected integration is still in the list (still active)
        const currentIntegrationStillActive = integrationsList.some(
          integration => integration.id === selectedIntegration
        )
        
        // If the current integration is no longer active or none was selected,
        // default to the first available integration
        if (!currentIntegrationStillActive || !selectedIntegration) {
          setSelectedIntegration(integrationsList[0].id)
        }
      } else {
        // No active integrations available
        setSelectedIntegration("")
      }
    } catch (error) {
      console.error("Error fetching integrations:", error)
      showNotification("Failed to load integrations", "error")
    }
  }

  // Hide notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [notification])

  const showNotification = (message, type = "success") => {
    setNotification({ message, type })
  }

  const handleSave = async () => {
    try {
      setSaving(true)

      // Basic validation
      if (!title.trim()) {
        showNotification("Title cannot be empty", "error")
        setSaving(false)
        return
      }

      if (!metaDescription.trim()) {
        showNotification("Meta description cannot be empty", "error")
        setSaving(false)
        return
      }

      if (!slug.trim()) {
        showNotification("Slug cannot be empty", "error")
        setSaving(false)
        return
      }

      // Get latest content before saving
      let contentToSave = htmlContent

      if (window.captureQuillContent) {
        const capturedContent = window.captureQuillContent()
        if (capturedContent && capturedContent.trim()) {
          contentToSave = capturedContent
          setHtmlContent(capturedContent) // Update local state
        }
      }

      if (!contentToSave || !contentToSave.trim()) {
        showNotification("Content cannot be empty", "error")
        setSaving(false)
        return
      }


      // Make API call to save
      await api.blogGeneration.updateGeneratedContent(id, {
        title: title.trim(),
        meta_description: metaDescription.trim(),
        content: contentToSave,
        slug: slug.trim()
      })

      // Update local state
      setContent({
        ...content,
        title: title.trim(),
        meta_description: metaDescription.trim(),
        content: contentToSave,
        slug: slug.trim()
      })

      // Show success message
      showNotification("Your content has been saved successfully")

      // Switch to view mode after a small delay to ensure state is updated
      setTimeout(() => {
        setIsEditing(false)
        setSaving(false)
      }, 100)
    } catch (error) {
      console.error("Error saving content:", error)
      showNotification(`Failed to save changes: ${error.response?.data?.error || error.message}`, "error")
      setSaving(false)
    }
  }

  const handleContentChange = (content) => {
    setHtmlContent(content)
  }

  // Toggle sidebar on mobile
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const handlePublish = async () => {
    if (!selectedIntegration) {
      showNotification("Please select an integration", "error")
      return
    }

    try {
      setPublishing(true)

      // Ensure we have the latest content
      let contentToPublish = htmlContent
      if (window.captureQuillContent) {
        const capturedContent = window.captureQuillContent()
        if (capturedContent && capturedContent.trim()) {
          contentToPublish = capturedContent
        }
      }

      const publishData = {
        integrationId: selectedIntegration,
        projectId: currentProject.id,
        content: contentToPublish || content.content,
        title: title || content.title,
        excerpt: metaDescription || content.meta_description,
        slug: slug || content.slug,
        metaDescription: metaDescription || content.meta_description,
        metaTitle: title || content.title
      }

      const result = await contentPublishService.publishToIntegration(publishData)
      
      // Get the integration name for the notification
      const integration = integrations.find(i => i.id === selectedIntegration)
      const integrationName = integration ? integration.name : 'selected platform'
      
      showNotification(`Published successfully to ${integrationName}`, "success")
    } catch (error) {
      console.error("Error publishing content:", error)
      showNotification(`Failed to publish: ${error.message || "Unknown error"}`, "error")
    } finally {
      setPublishing(false)
    }
  }

  const handleStatusUpdate = async () => {
    try {
      setStatusUpdating(true)
      
      // Call API to update content status
      await api.blogGeneration.updateGeneratedContent(id, {
        title: title || content.title,
        meta_description: metaDescription || content.meta_description,
        content: content.content,
        slug: slug || content.slug,
        status: contentStatus
      })
      
      // Update local state
      setContent({
        ...content,
        status: contentStatus
      })
      
      showNotification(`Status updated to ${contentStatus}`, "success")
    } catch (error) {
      console.error("Error updating status:", error)
      showNotification("Failed to update status", "error")
    } finally {
      setStatusUpdating(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="flex flex-col items-center">
          <svg
            className="animate-spin h-10 w-10 text-primary-500 mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <div className="text-lg text-gray-600 font-medium">Loading content...</div>
        </div>
      </div>
    )
  }

  if (!content) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-sm mt-8">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Content Not Found</h2>
          <p className="text-gray-600 mb-6">{error || "We couldn't find the content you're looking for."}</p>
          <Link
            to="/dashboard/competitors"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowLeft size={16} className="mr-2" />
            Return to Competitors
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50">
      {/* Header with Back Button, Title and Action Buttons */}
      <div className="bg-white border-b border-gray-200 p-4 flex items-center shadow-sm z-10">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-primary-600 p-2 mr-2 rounded-full hover:bg-gray-100 flex-shrink-0"
          title="Back"
        >
          <ArrowLeft size={18} />
        </button>

        <h1 className="text-xl font-bold text-gray-800 truncate flex-1">
          {isEditing ? "Edit Content" : content.title}
        </h1>

        {/* Action Buttons */}
        <div className="flex space-x-2 flex-shrink-0">
          {isEditing ? (
            <>
              <button
                onClick={() => {
                  if (window.captureQuillContent) {
                    const content = window.captureQuillContent()
                    if (content) {
                      setHtmlContent(content)
                    }
                  }
                  handleSave()
                }}
                disabled={saving}
                className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:bg-primary-400 shadow-sm"
              >
                {saving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Save
                  </>
                )}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="flex items-center px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium shadow-sm"
              >
                <X size={16} className="mr-2" />
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium shadow-sm"
            >
              <Edit size={16} className="mr-2" />
              Edit
            </button>
          )}

          {/* Mobile sidebar toggle */}
          <button
            className="md:hidden flex items-center justify-center p-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={toggleSidebar}
          >
            {sidebarOpen ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </div>

      {/* Main Content Area with Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 overflow-auto p-4 md:p-6">
          <div className="w-full h-full flex flex-col">
            {isEditing ? (
              <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col h-full overflow-auto">
                {/* Title field */}
                <div className="mb-6">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value)
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 shadow-sm"
                    placeholder="Enter article title"
                  />
                </div>

                {/* Quill rich text editor - using flex-1 to fill available space */}
                <div className="flex flex-col flex-grow mb-0">
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                    Content
                  </label>

                  <div className="border border-gray-300 rounded-lg overflow-hidden shadow-sm flex-grow" style={{ display: 'flex', flexDirection: 'column', minHeight: '500px' }}>
                    <VanillaQuillEditor
                      value={htmlContent}
                      onChange={handleContentChange}
                      placeholder="Write your content here..."
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm overflow-auto h-full">
                <div className="p-6 md:p-8">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{content.title}</h1>
                  
                  <div className="prose prose-blue max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: content.content }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Hidden on mobile unless toggled */}
        <div
          className={`${
            sidebarOpen ? "translate-x-0" : "translate-x-full"
          } md:translate-x-0 fixed md:relative right-0 top-[65px] md:top-0 bottom-0 w-72 bg-white border-l border-gray-200 overflow-y-auto transition-transform duration-300 ease-in-out shadow-lg md:shadow-none z-20`}
        >
          <div className="p-5">
            <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4">Content Information</h3>

            {/* Thumbnail Image */}
            {content.thumbnail_url && (
              <div className="mb-5">
                <h4 className="text-xs font-medium text-gray-500 mb-1.5">Thumbnail:</h4>
                <div className="bg-gray-50 rounded-lg p-2 border border-gray-200 shadow-sm">
                  <img 
                    src={content.thumbnail_url} 
                    alt="Thumbnail" 
                    className="w-full h-auto rounded-md"
                  />
                </div>
              </div>
            )}

            {/* Source URL */}
            {content.competitor_url && (
              <div className="mb-5">
                <h4 className="text-xs font-medium text-gray-500 mb-1.5">Source URL:</h4>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 shadow-sm">
                  <a 
                    href={content.competitor_url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm text-primary-600 hover:text-primary-700 break-all"
                  >
                    {content.competitor_url}
                  </a>
                </div>
              </div>
            )}

            {/* Status */}
            <div className="mb-5">
              <h4 className="text-xs font-medium text-gray-500 mb-1.5">Status:</h4>
              <div className="flex items-center space-x-2">
                <select
                  value={contentStatus}
                  onChange={(e) => setContentStatus(e.target.value)}
                  className="flex-1 text-sm border border-gray-300 rounded-md p-2 bg-white"
                >
                  <option value="draft">Draft</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="published">Published</option>
                </select>
                <button
                  onClick={handleStatusUpdate}
                  disabled={statusUpdating}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-xs font-medium border border-gray-300 disabled:opacity-50"
                >
                  {statusUpdating ? "Saving..." : "Save"}
                </button>
              </div>
            </div>

            {/* Integrations */}
            <div className="mb-5">
              <h4 className="text-xs font-medium text-gray-500 mb-1.5">Integrations:</h4>
              <div className="flex flex-col space-y-2">
                <select
                  value={selectedIntegration}
                  onChange={(e) => setSelectedIntegration(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-md p-2 bg-white"
                  disabled={integrations.length === 0}
                >
                  {integrations.length === 0 ? (
                    <option value="">No integrations available</option>
                  ) : (
                    integrations.map((integration) => (
                      <option key={integration.id} value={integration.id}>
                        {integration.name} ({integration.type})
                      </option>
                    ))
                  )}
                </select>
                <button
                  onClick={handlePublish}
                  disabled={publishing || !selectedIntegration}
                  className="w-full px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium disabled:bg-primary-400 disabled:cursor-not-allowed"
                >
                  {publishing ? "Publishing..." : "Publish"}
                </button>
              </div>
            </div>

            {/* Meta Description */}
            {isEditing ? (
              <div className="mb-5">
                <label htmlFor="metaDescription" className="block text-xs font-medium text-gray-500 mb-1.5">
                  Meta Description:
                </label>
                <textarea
                  id="metaDescription"
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  rows={5}
                  className="w-full p-3 text-sm border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 shadow-sm"
                  placeholder="Enter meta description for SEO"
                />
                <p className="mt-1 text-xs text-gray-500">{metaDescription.length}/160 characters recommended</p>
              </div>
            ) : (
              <div className="mb-5">
                <h4 className="text-xs font-medium text-gray-500 mb-1.5">Meta Description:</h4>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 shadow-sm">
                  <p className="text-sm text-gray-800">{content.meta_description}</p>
                </div>
              </div>
            )}

            {/* Slug */}
            {isEditing ? (
              <div className="mb-5">
                <label htmlFor="slug" className="block text-xs font-medium text-gray-500 mb-1.5">
                  Slug:
                </label>
                <input
                  id="slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""))}
                  className="w-full p-3 text-sm border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 shadow-sm"
                  placeholder="Enter URL slug"
                />
                <p className="mt-1 text-xs text-gray-500">Used in the URL of your article</p>
              </div>
            ) : (
              <div className="mb-5">
                <h4 className="text-xs font-medium text-gray-500 mb-1.5">Slug:</h4>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 shadow-sm">
                  <p className="text-sm text-gray-800 break-all">{content.slug || slug}</p>
                </div>
              </div>
            )}

            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">Details</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-500">Created</span>
                  <span className="text-xs font-medium">{new Date(content.created_at).toLocaleDateString()}</span>
                </div>
                {content.updated_at && content.updated_at !== content.created_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Last Updated</span>
                    <span className="text-xs font-medium">{new Date(content.updated_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Popup - Centered at top */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-4 shadow-xl border border-gray-200 max-w-sm flex items-center"
            >
              {notification.type === "success" ? (
                <CheckCircle className="text-emerald-500 mr-3 flex-shrink-0" size={20} />
              ) : (
                <AlertCircle className="text-red-500 mr-3 flex-shrink-0" size={20} />
              )}
              <div>
                <h3 className={`text-sm font-medium ${notification.type === "success" ? "text-gray-900" : "text-red-700"}`}>
                  {notification.type === "success" ? "Success!" : "Error"}
                </h3>
                <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default GeneratedContentView 